// Per-page long-form SEO content. Each entry adds 600+ words of
// keyword-rich material (paragraphs, H3 sections, lists, FAQs) on top of
// the existing page content. Targeted keywords:
// "best travel agency in Kashmir", "Kashmir tour packages", "Gulmarg",
// "Pahalgam", "Sonmarg/Sonamarg", "Srinagar", "Leh-Ladakh".

export const homeSEO = {
  title: 'Plan Your Kashmir Trip with the Best Travel Agency in Kashmir',
  intro: [
    'Looking to plan an unforgettable Kashmir trip? <strong>Velmora Holidays</strong> is the <strong>best travel agency in Kashmir</strong>, headquartered in Srinagar and trusted by thousands of travellers across India and abroad. From luxury <strong>Kashmir tour packages</strong> for honeymooners to value-for-money family itineraries and adrenaline-loaded adventure trips, we design every journey around what you actually want to experience — not a one-size-fits-all template.',
    'Our local <strong>Kashmir tour experts</strong> have grown up exploring the valleys of <strong>Gulmarg</strong>, <strong>Pahalgam</strong>, <strong>Sonmarg</strong>, <strong>Srinagar</strong> and the high-altitude desert of <strong>Leh-Ladakh</strong>. That on-ground knowledge means better hotel choices, smarter routes, fairer pricing on shikara rides, gondola tickets and pony treks, and 24×7 support during your trip.',
  ],
  sections: [
    {
      heading: 'Top Kashmir Destinations We Cover',
      paragraphs: [
        'Every Velmora itinerary is a curated blend of must-see icons and offbeat gems. Whether you have 4 days or 12, we make sure you experience the full range of what Kashmir has to offer.',
      ],
      list: [
        '<strong>Srinagar:</strong> Dal Lake shikara rides, Mughal Gardens (Nishat, Shalimar, Chashme Shahi), Hazratbal Shrine and authentic Kashmiri Wazwan.',
        '<strong>Gulmarg:</strong> Asia’s highest cable car (Gondola Phase 1 & 2), skiing in winter, alpine meadows in summer, and the historic Gulmarg golf course.',
        '<strong>Pahalgam:</strong> Betaab Valley, Aru Valley, Chandanwari, river rafting on the Lidder, and base camp for the Amarnath Yatra.',
        '<strong>Sonmarg (Sonamarg):</strong> Thajiwas Glacier, Zojila Pass views, and gateway to Ladakh during summer.',
        '<strong>Leh-Ladakh:</strong> Pangong Lake, Nubra Valley, Khardung La, Magnetic Hill and ancient monasteries — bookable as standalone or combined Kashmir + Ladakh packages.',
        '<strong>Doodhpathri, Yousmarg & Gurez Valley:</strong> Offbeat alpine getaways for travellers who want to skip the crowds.',
      ],
    },
    {
      heading: 'Kashmir Tour Packages for Every Kind of Traveller',
      paragraphs: [
        'Our <strong>Kashmir tour packages</strong> are organised by traveller intent so you can quickly find what suits you:',
      ],
      list: [
        '<strong>Kashmir honeymoon packages</strong> — premium houseboats on Dal Lake, candle-lit dinners in Pahalgam and private shikara rides.',
        '<strong>Kashmir family packages</strong> — kid-friendly itineraries with comfortable cab transfers, snow play in Gulmarg and easy walks in Pahalgam.',
        '<strong>Kashmir adventure tours</strong> — skiing, snowboarding, trekking to Tarsar Marsar, river rafting and high-altitude biking.',
        '<strong>Kashmir + Leh-Ladakh combo</strong> — the bucket-list North India circuit covering Srinagar, Sonmarg, Kargil, Leh, Nubra and Pangong.',
        '<strong>Group & MICE tours</strong> — corporate offsites, college trips and pilgrim groups with bulk-rate hotel and transport contracts.',
      ],
    },
    {
      heading: 'Why Velmora Holidays is the Best Travel Agency in Kashmir',
      list: [
        '<strong>Local ownership:</strong> A Srinagar-based team that lives and breathes Kashmir tourism — not a reseller running things from another city.',
        '<strong>Transparent pricing:</strong> No hidden charges, no surprise upgrades. Every <strong>Kashmir holiday package</strong> quote is itemised.',
        '<strong>Hand-picked hotels & houseboats:</strong> Verified properties, real reviews, and category options from budget to 5-star.',
        '<strong>Reliable transport:</strong> Well-maintained sedans, SUVs and Tempo Travellers driven by experienced Kashmiri drivers who double as informal guides.',
        '<strong>24×7 support:</strong> A real human on WhatsApp and phone throughout your trip, not a chatbot.',
        '<strong>Verified reviews:</strong> 4.9★ ratings from 1000+ travellers who have already explored Kashmir with us.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Which is the best travel agency in Kashmir for tour packages?',
      a: 'Velmora Holidays is consistently rated as one of the best travel agencies in Kashmir. We are based in Srinagar, locally owned, and specialise in customised Kashmir tour packages covering Gulmarg, Pahalgam, Sonmarg, Srinagar and Leh-Ladakh, with a 4.9★ rating from 1000+ travellers.',
    },
    {
      q: 'How much does a Kashmir tour package cost?',
      a: 'A typical 5N/6D Kashmir tour package starts from around ₹14,999 per person on twin-sharing for budget hotels, ₹22,000+ for premium 4-star stays and ₹35,000+ for luxury houseboats and 5-star hotels. The final price depends on dates, group size, hotel category and inclusions like Gulmarg Gondola tickets.',
    },
    {
      q: 'What is the best time to visit Kashmir?',
      a: 'Kashmir is a year-round destination. March–May is ideal for tulips and pleasant weather, June–August for escaping the heat and exploring meadows, September–November for autumn colours, and December–February for snowfall, skiing in Gulmarg and frozen Sonmarg landscapes.',
    },
    {
      q: 'Do your packages cover Leh-Ladakh too?',
      a: 'Yes. We offer dedicated Leh-Ladakh packages as well as combined Kashmir + Ladakh circuits via the Srinagar–Sonmarg–Kargil–Leh route. These are operated only when the Zojila Pass is open (typically May to October).',
    },
    {
      q: 'Is Kashmir safe for tourists and families?',
      a: 'Kashmir is safe for tourists. Tourist areas like Srinagar, Gulmarg, Pahalgam and Sonmarg see lakhs of visitors every year. Velmora Holidays uses verified hotels, experienced drivers and provides 24×7 on-ground support throughout your trip.',
    },
    {
      q: 'Can I customise my Kashmir itinerary?',
      a: 'Absolutely. Every Velmora itinerary is fully customisable — you can add or skip destinations, change hotel categories, extend nights in Pahalgam or Gulmarg, or combine Kashmir with Leh-Ladakh, Vaishno Devi or Amritsar.',
    },
  ],
};

export const aboutSEO = {
  title: 'About Velmora Holidays — Your Kashmir Tour Experts in Srinagar',
  intro: [
    '<strong>Velmora Holidays</strong> is a homegrown <strong>travel agency in Kashmir</strong> founded by a team of Kashmiris who believe their valley deserves to be experienced beyond the standard tourist trail. Operating out of Khayam Chowk in <strong>Srinagar</strong>, we have spent over a decade designing the <strong>best Kashmir tour packages</strong> — combining hospitality, safety, and authentic local experiences in a way that only locals can.',
    'We started small: one car, a handful of houseboats on Dal Lake, and a promise to never overcharge or oversell. Today, we are recognised as one of the <strong>best travel agencies in Kashmir</strong>, trusted by honeymooners, families, solo travellers and corporates across India and abroad.',
  ],
  sections: [
    {
      heading: 'Our Mission as Kashmir Tour Experts',
      paragraphs: [
        'Our mission is simple: make every Kashmir trip easy, transparent and unforgettable. From the moment you enquire to the moment you reach back home, you deal with one dedicated travel consultant who personally owns your itinerary. No call-centre handoffs, no surprise costs.',
        'We take pride in being more than a booking platform. We are storytellers, route planners, hotel curators and on-ground problem solvers — all rolled into the team you would want as a local friend in Kashmir.',
      ],
    },
    {
      heading: 'Destinations We Specialise In',
      list: [
        '<strong>Srinagar:</strong> Dal Lake, Nigeen Lake, Mughal Gardens, Old City heritage walks, Wazwan food trails.',
        '<strong>Gulmarg:</strong> Gondola, skiing courses, snow trekking, summer meadow camping.',
        '<strong>Pahalgam:</strong> Betaab, Aru and Chandanwari valleys, Lidder rafting, pony treks.',
        '<strong>Sonmarg:</strong> Thajiwas Glacier, alpine treks, day trips to Zojila viewpoint.',
        '<strong>Leh-Ladakh:</strong> Pangong, Nubra, Khardung La, Hemis and Thiksey monasteries.',
        '<strong>Offbeat:</strong> Doodhpathri, Yousmarg, Gurez, Bangus Valley and Lolab.',
      ],
    },
    {
      heading: 'What Makes Us Different',
      list: [
        '<strong>Local-first team:</strong> Every consultant, driver and guide is from Kashmir.',
        '<strong>Custom itineraries:</strong> No fixed group departures — every <strong>Kashmir holiday package</strong> is built around you.',
        '<strong>Verified inventory:</strong> We personally inspect hotels and houseboats before adding them.',
        '<strong>Fair pricing:</strong> We share itemised quotes and refund unused services where possible.',
        '<strong>Responsible tourism:</strong> We support local artisans, drivers and small homestays in remote villages.',
      ],
    },
    {
      heading: 'A Decade of Trust',
      paragraphs: [
        'With 1000+ happy travellers, 4.9★ average ratings and a steady stream of repeat customers and referrals, we have grown the right way — through word-of-mouth and genuine experiences, not paid noise. When people search for the <strong>best travel agency in Kashmir</strong>, our goal is to be the name they hear from friends and family first.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Where is Velmora Holidays located?',
      a: 'Our office is at Khayam Chowk, near Hotel Royal Batoo, Srinagar, Jammu and Kashmir 190001. We operate across Kashmir and Ladakh.',
    },
    {
      q: 'How long has Velmora Holidays been in business?',
      a: 'We have been designing Kashmir tour packages for over a decade and have served 1000+ travellers across India and abroad.',
    },
    {
      q: 'Do you handle airport pickups and drops in Srinagar?',
      a: 'Yes — every Velmora Kashmir tour package includes Srinagar airport pickup and drop with a private vehicle of the chosen category.',
    },
    {
      q: 'Can I meet your team in person before booking?',
      a: 'Absolutely. Walk into our Srinagar office at Khayam Chowk during business hours (Mon–Sat, 9 AM–8 PM) and we will be happy to plan your Kashmir trip face-to-face.',
    },
    {
      q: 'Do you offer English-speaking guides?',
      a: 'Yes. We provide English, Hindi and Urdu-speaking drivers and dedicated tour guides on request for sightseeing in Srinagar, Gulmarg, Pahalgam and Sonmarg.',
    },
  ],
};

export const destinationsSEO = {
  title: 'Kashmir Destinations — A Complete Guide by Velmora Holidays',
  intro: [
    'Kashmir is not a single destination — it is a chain of stunning landscapes, each with its own personality. As the <strong>best travel agency in Kashmir</strong>, we have built itineraries around every major valley so you spend less time on logistics and more time soaking in the views. Below is your quick guide to the destinations we cover in our <strong>Kashmir tour packages</strong>.',
  ],
  sections: [
    {
      heading: 'Srinagar — The Heart of Kashmir',
      paragraphs: [
        'Srinagar is the gateway to Kashmir and the base for almost every <strong>Kashmir holiday package</strong>. Stay overnight in a deluxe houseboat on <strong>Dal Lake</strong> or <strong>Nigeen Lake</strong>, take a sunrise shikara ride, and walk through the Mughal Gardens — Nishat Bagh, Shalimar Bagh and Chashme Shahi — built over 400 years ago.',
        'Don’t miss Hazratbal Shrine, Jamia Masjid in the Old City, the floating vegetable market at dawn, and a traditional Wazwan dinner with rogan josh, gushtaba and seekh tujj.',
      ],
    },
    {
      heading: 'Gulmarg — The Meadow of Flowers',
      paragraphs: [
        '<strong>Gulmarg</strong> sits at 2,650m and transforms with the seasons. In winter (Dec–Feb), it is one of Asia’s top ski destinations with powder snow and the Gulmarg Gondola — the second-highest cable car in the world. In summer, it becomes a lush alpine meadow ideal for golfing, horse riding and easy hikes to Khilanmarg.',
        'Most Velmora Gulmarg itineraries include Gondola Phase 1 & 2 tickets, snow activities at Kongdoori, and optional skiing lessons with certified instructors.',
      ],
    },
    {
      heading: 'Pahalgam — The Valley of Shepherds',
      paragraphs: [
        '<strong>Pahalgam</strong> is a riverside town along the Lidder, surrounded by pine forests and snow-fed streams. The big three sub-valleys — <strong>Betaab Valley</strong>, <strong>Aru Valley</strong> and <strong>Chandanwari</strong> — are usually covered together as a full-day local sightseeing circuit.',
        'For adventure lovers, Pahalgam offers white-water rafting on the Lidder, pony rides to Baisaran (Mini Switzerland), and treks toward Tarsar Marsar. Pahalgam is also the base camp for the Amarnath Yatra.',
      ],
    },
    {
      heading: 'Sonmarg — The Meadow of Gold',
      paragraphs: [
        '<strong>Sonmarg</strong> (also spelled Sonamarg) is a high-altitude meadow at 2,800m, famous for the <strong>Thajiwas Glacier</strong>, where you can play in snow even in summer. It is also the last major town on the Srinagar–Leh highway and a strategic gateway to Ladakh once the Zojila Pass opens (May–October).',
      ],
    },
    {
      heading: 'Leh-Ladakh — The Cold Desert',
      paragraphs: [
        'Looking to combine Kashmir with the high-altitude moonscape of <strong>Leh-Ladakh</strong>? We run dedicated Ladakh packages and combined Kashmir + Ladakh tours that travel via the dramatic Srinagar–Sonmarg–Kargil–Leh road. Highlights include <strong>Pangong Lake</strong>, <strong>Nubra Valley</strong>, <strong>Khardung La</strong>, Magnetic Hill and ancient monasteries like Thiksey, Hemis and Diskit.',
      ],
    },
    {
      heading: 'Offbeat Kashmir',
      list: [
        '<strong>Doodhpathri</strong> — the “Valley of Milk”, a quiet meadow only 50 km from Srinagar.',
        '<strong>Yousmarg</strong> — pine forests, Doodh Ganga river and easy day-trip access from Srinagar.',
        '<strong>Gurez Valley</strong> — a remote Himalayan valley near the LOC with Dard-Shin culture.',
        '<strong>Bangus Valley</strong> & <strong>Lolab Valley</strong> — pristine and barely-touristed alpine landscapes.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Which Kashmir destination is best for first-time travellers?',
      a: 'Most first-time travellers do the classic Srinagar + Gulmarg + Pahalgam + Sonmarg circuit. It covers Dal Lake, snow at Gulmarg, valleys at Pahalgam and the Thajiwas Glacier at Sonmarg in 5–6 days.',
    },
    {
      q: 'Can I see snow in Kashmir in summer?',
      a: 'Yes. Even in May–August you can find snow at Thajiwas Glacier in Sonmarg, Affarwat peak in Gulmarg (via Gondola Phase 2), and some passes around Ladakh.',
    },
    {
      q: 'Is Pahalgam better than Gulmarg?',
      a: 'They are different experiences. Gulmarg is best for snow, skiing and high-altitude views. Pahalgam is greener, riverside and family-friendly. We strongly recommend covering both in your Kashmir tour package.',
    },
    {
      q: 'How many days are enough for a Kashmir trip?',
      a: 'For Kashmir alone, 5–7 days is ideal. For a Kashmir + Leh-Ladakh combination, plan 10–12 days minimum to acclimatise properly to high altitude.',
    },
    {
      q: 'Do I need permits for any Kashmir destinations?',
      a: 'Kashmir Valley does not need any special permit for Indian citizens. For Ladakh, certain inner-line areas like Pangong, Nubra and Tso Moriri require permits, which we arrange as part of our packages.',
    },
  ],
};

export const packagesSEO = {
  title: 'Best Kashmir Tour Packages — Compare, Customise & Book',
  intro: [
    'Browse our most-booked <strong>Kashmir tour packages</strong> covering Srinagar, Gulmarg, Pahalgam, Sonmarg and Leh-Ladakh. As the <strong>best travel agency in Kashmir</strong>, we keep our packages flexible — you can change hotel categories, swap destinations, add extra nights or combine itineraries.',
    'All Velmora <strong>Kashmir holiday packages</strong> include accommodation, daily breakfast, private cab transfers, sightseeing as per the itinerary and 24×7 on-ground support. Permits, monument entry tickets and Gondola tickets can be added on request.',
  ],
  sections: [
    {
      heading: 'Popular Kashmir Tour Package Categories',
      list: [
        '<strong>Kashmir Honeymoon Packages (4N/5D – 6N/7D):</strong> Premium houseboats on Dal Lake, candle-lit dinners, private shikara rides, Pahalgam riverside cottages.',
        '<strong>Kashmir Family Packages (5N/6D):</strong> Comfortable hotels, slow-paced sightseeing, snow play in Gulmarg, kid-friendly Pahalgam valleys.',
        '<strong>Kashmir Group & MICE Tours:</strong> Bulk discounts on hotels and tempo travellers for groups of 10+.',
        '<strong>Kashmir Adventure Tours:</strong> Skiing in Gulmarg, river rafting in Pahalgam, treks to Tarsar Marsar and Kashmir Great Lakes.',
        '<strong>Kashmir + Vaishno Devi Combo:</strong> Add 2 nights of Mata Vaishno Devi darshan to your Kashmir itinerary.',
        '<strong>Kashmir + Leh-Ladakh Tours:</strong> 10–12 day combined circuits via the iconic Srinagar–Leh highway.',
      ],
    },
    {
      heading: 'What’s Included in Our Kashmir Packages',
      list: [
        'Accommodation in chosen hotel/houseboat category',
        'Daily breakfast (some packages include dinner)',
        'All inter-city and local sightseeing transfers in a private cab',
        'Airport pickup and drop in Srinagar',
        'Sightseeing as per the itinerary',
        'On-ground 24×7 support from our Srinagar office',
      ],
    },
    {
      heading: 'What’s Generally Excluded',
      list: [
        'Flights to/from Srinagar',
        'Personal expenses, tips and laundry',
        'Gondola tickets in Gulmarg, pony rides, shikara rides (can be added on request)',
        'Monument entry fees',
        'Anything not explicitly mentioned in the package inclusions',
      ],
    },
    {
      heading: 'How to Book Your Kashmir Tour Package',
      paragraphs: [
        'Booking is simple: tell us your travel dates, number of travellers, hotel preference and any must-do experiences. We will share 1–2 quote options within hours, refine the itinerary with you, and confirm with a 25% advance. Balance is paid on arrival or 7 days before travel.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What is the price of a 5N/6D Kashmir tour package?',
      a: 'Our 5N/6D Kashmir tour packages start from around ₹14,999 per person on twin-sharing for budget hotels, ₹22,000+ for 4-star stays and ₹35,000+ for premium houseboats and 5-star resorts. Final pricing depends on travel dates and group size.',
    },
    {
      q: 'Can I book a Kashmir tour package without flights?',
      a: 'Yes. All our Kashmir tour packages are land-only by default. We can add flights from any Indian city on request, with prices quoted at live rates.',
    },
    {
      q: 'Do honeymoon packages include candlelight dinner and decoration?',
      a: 'Yes — Velmora honeymoon packages include flower decoration on arrival, complimentary cake and one candlelight dinner. You can upgrade to private shikara dinners or Pahalgam riverside set-ups.',
    },
    {
      q: 'Are Gondola tickets in Gulmarg included in the package?',
      a: 'Gondola tickets are usually quoted separately as availability and prices fluctuate. We can pre-book Phase 1 and Phase 2 tickets on your behalf so you skip the long ticket queue at Gulmarg.',
    },
    {
      q: 'How early should I book my Kashmir tour package?',
      a: 'For peak season (April–June and December–January for snow), we recommend booking at least 30–45 days in advance to lock in the best hotel rates and availability.',
    },
  ],
};

export const contactSEO = {
  title: 'Plan Your Kashmir Trip — Talk to a Local Tour Expert',
  intro: [
    'Whether you’re planning a 5-day Kashmir tour, a romantic honeymoon, or an extended Kashmir + Ladakh adventure, our team is here to help. As the <strong>best travel agency in Kashmir</strong>, we believe in fast, honest and human responses — most enquiries get a personalised itinerary within a few hours.',
    'Reach us by phone, WhatsApp or email, or simply walk into our Srinagar office at Khayam Chowk. We are happy to plan your Kashmir trip face-to-face if you are already in town.',
  ],
  sections: [
    {
      heading: 'How We Help You Plan',
      list: [
        'Free consultation on the best Kashmir destinations for your dates and budget',
        'Multiple itinerary and hotel-category options to compare side by side',
        'Real-time price updates for hotels, transport and Gondola/shikara experiences',
        'Add-ons like Vaishno Devi, Amritsar or Leh-Ladakh combined into one package',
        'Group, MICE and corporate bookings with bulk-rate quotes',
      ],
    },
    {
      heading: 'Where We Operate',
      paragraphs: [
        'We operate across the entire Kashmir Valley including <strong>Srinagar</strong>, <strong>Gulmarg</strong>, <strong>Pahalgam</strong>, <strong>Sonmarg</strong>, Doodhpathri, Yousmarg and Gurez. Our Ladakh operations cover Leh, Nubra, Pangong, Tso Moriri and Kargil during the open road season (typically May–October).',
      ],
    },
  ],
  faqs: [
    {
      q: 'How quickly will I get a quote for my Kashmir tour?',
      a: 'Most enquiries receive a customised quote with 1–2 itinerary options within 4–6 working hours. Urgent bookings are handled the same day.',
    },
    {
      q: 'Can I pay online for my Kashmir package?',
      a: 'Yes. We accept UPI, NEFT/IMPS bank transfers and credit/debit card payments. A 25% advance confirms your booking; the balance is due 7 days before travel.',
    },
    {
      q: 'Do you offer last-minute Kashmir bookings?',
      a: 'Yes. Walk-in and last-minute bookings are welcome, subject to hotel availability. Our Srinagar office can put together a 4–7 day Kashmir itinerary in a single sitting.',
    },
    {
      q: 'Can I visit your office in Srinagar?',
      a: 'Yes — our office is open Monday to Saturday, 9 AM to 8 PM at Khayam Chowk, near Hotel Royal Batoo, Srinagar.',
    },
  ],
};

export const blogSEO = {
  title: 'Kashmir Travel Guides, Tips & Stories from Local Experts',
  intro: [
    'Welcome to the Velmora Holidays travel blog — your go-to resource for planning the perfect Kashmir trip. Written by our in-house <strong>Kashmir tour experts</strong>, every guide is based on first-hand experience exploring <strong>Srinagar</strong>, <strong>Gulmarg</strong>, <strong>Pahalgam</strong>, <strong>Sonmarg</strong> and <strong>Leh-Ladakh</strong>.',
    'Whether you’re researching the <strong>best time to visit Kashmir</strong>, packing for a snowfall trip, comparing <strong>Kashmir tour packages</strong> or looking for offbeat alternatives to crowded valleys, our articles will save you hours of Googling.',
  ],
  sections: [
    {
      heading: 'What You’ll Find on Our Blog',
      list: [
        '<strong>Destination guides</strong> — deep dives into Srinagar, Gulmarg, Pahalgam, Sonmarg, Leh-Ladakh and offbeat valleys.',
        '<strong>Seasonal travel tips</strong> — what Kashmir is like in every month, and which package fits each season.',
        '<strong>Packing lists & pro tips</strong> — winter snow gear, summer essentials, and dos & don’ts in Kashmir.',
        '<strong>Food & culture</strong> — Wazwan dishes, Kashmiri tea (Kahwa & Noon Chai), local festivals and craft markets.',
        '<strong>Itinerary ideas</strong> — 4N/5D, 5N/6D, 7N/8D and 10N/11D Kashmir + Ladakh sample plans.',
      ],
    },
    {
      heading: 'Why Read Travel Tips From a Local Agency',
      paragraphs: [
        'Generic travel sites recycle the same listicles every season. Our blog, on the other hand, is written by people who actually drive these routes, stay in these hotels and ride these gondolas multiple times every year. That means honest reviews, real prices and practical tips you can use.',
      ],
    },
  ],
  faqs: [
    {
      q: 'How often do you publish new Kashmir travel articles?',
      a: 'We publish 1–2 new in-depth Kashmir travel guides every month, plus seasonal updates on snowfall, road status, Amarnath Yatra timings and tulip garden bloom dates.',
    },
    {
      q: 'Can I request a topic for the blog?',
      a: 'Yes — drop us an email at info@velmoraholidays.com with the topic you’d like covered. We regularly write articles based on real reader questions.',
    },
    {
      q: 'Are the blog itineraries bookable?',
      a: 'Yes. Every sample itinerary on our blog can be booked or customised as a Velmora Kashmir tour package. Just share the article link with our team.',
    },
  ],
};

export const faqsSEO = {
  title: 'Everything You Need to Know Before Booking a Kashmir Tour',
  intro: [
    'This page collects the most common questions we receive every week from travellers planning a trip with the <strong>best travel agency in Kashmir</strong>. If you’re comparing <strong>Kashmir tour packages</strong>, deciding on the best time to visit, or wondering whether to combine Kashmir with <strong>Leh-Ladakh</strong>, you should find your answer below.',
    'Can’t find what you are looking for? Our team is one phone call or WhatsApp message away — and we genuinely enjoy helping travellers plan, even before a booking is made.',
  ],
  sections: [
    {
      heading: 'Quick Reference for First-Time Kashmir Travellers',
      list: [
        '<strong>Best base city:</strong> Srinagar — most flights land here and most itineraries start/end here.',
        '<strong>Must-cover destinations:</strong> Srinagar, Gulmarg, Pahalgam and Sonmarg (the “Big 4”).',
        '<strong>Recommended trip length:</strong> 5–7 days for Kashmir; 10–12 days for Kashmir + Leh-Ladakh.',
        '<strong>Currency & payments:</strong> INR. UPI, cards and cash are widely accepted in tourist areas.',
        '<strong>Connectivity:</strong> Postpaid SIMs work everywhere; prepaid SIMs of other states do not work in J&K.',
      ],
    },
  ],
  faqs: [
    {
      q: 'What documents do I need to travel to Kashmir?',
      a: 'Indian citizens need a valid government-issued photo ID (Aadhaar, Passport, Voter ID, Driving License). International travellers need a valid passport and Indian visa. No special permit is required for the Kashmir Valley.',
    },
    {
      q: 'Will my prepaid SIM work in Kashmir?',
      a: 'Prepaid SIMs issued outside Jammu & Kashmir do not work in the region. We recommend carrying a postpaid SIM (Jio, Airtel or BSNL) before your trip. Our drivers and hotels also offer Wi-Fi.',
    },
    {
      q: 'How do I reach Kashmir from other Indian cities?',
      a: 'Daily direct flights operate to Srinagar International Airport (SXR) from Delhi, Mumbai, Bengaluru, Hyderabad, Kolkata and Chennai. You can also reach Jammu by train and drive 7–8 hours to Srinagar.',
    },
    {
      q: 'Is internet available throughout the trip?',
      a: 'Yes — most hotels in Srinagar, Gulmarg, Pahalgam and Sonmarg offer Wi-Fi. Mobile data is reliable on postpaid networks across all major tourist areas.',
    },
    {
      q: 'Is it okay to travel with kids and senior citizens?',
      a: 'Absolutely. We design specific family-friendly Kashmir tour packages with comfortable transfers, slow-paced sightseeing, ground-floor hotel rooms for seniors, and oxygen support on Ladakh extensions.',
    },
    {
      q: 'Do you arrange skiing and adventure activities in Gulmarg?',
      a: 'Yes. We tie up with certified skiing instructors and adventure operators in Gulmarg for half-day, full-day and multi-day skiing courses, snowboarding sessions and snow trekking.',
    },
  ],
};

export const privacySEO = {
  title: 'How Velmora Holidays Protects Your Data',
  intro: [
    'When you book <strong>Kashmir tour packages</strong> with us, you trust Velmora Holidays with personal information like your contact details, ID proof and payment data. As the <strong>best travel agency in Kashmir</strong>, we take this responsibility seriously and follow strict privacy practices in line with applicable Indian data protection laws.',
    'This privacy summary complements the full policy above and explains, in plain language, how your information is used and safeguarded across our website and offline operations.',
  ],
  sections: [
    {
      heading: 'Plain-English Summary',
      list: [
        'We collect only the information needed to plan, book and operate your Kashmir trip.',
        'We never sell or rent your data to third parties for marketing.',
        'Hotels, transport providers and airlines receive only the minimum data needed for your booking.',
        'You can request a copy of your data, ask us to correct it, or delete it (subject to legal retention rules).',
        'All payment data is handled by certified, PCI-compliant payment gateways — we never store your full card details on our servers.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Will my data be shared with hotels and transporters?',
      a: 'Only the minimum information required for check-in and travel — typically your name, age and ID — is shared with the hotels, houseboats and transport providers on your itinerary.',
    },
    {
      q: 'How do I request deletion of my data?',
      a: 'Email info@velmoraholidays.com with the subject “Data Deletion Request”. We will process valid requests within 30 days, subject to legal accounting and tax retention rules.',
    },
    {
      q: 'Do you use cookies?',
      a: 'Yes — we use essential and analytics cookies to improve our website experience. You can disable cookies in your browser settings; some features may not work as expected.',
    },
  ],
};

export const refundSEO = {
  title: 'Cancellations & Refunds Made Simple',
  intro: [
    'We understand that plans change. As the <strong>best travel agency in Kashmir</strong>, our refund policy is designed to be as fair and transparent as possible while still respecting the non-refundable commitments we make to hotels, airlines and transporters on your behalf.',
    'The cancellation grid above explains the standard charges. The points below cover the most common edge cases we get asked about — peak-season blackout dates, force majeure, partial cancellations and modification requests.',
  ],
  sections: [
    {
      heading: 'Things to Keep in Mind',
      list: [
        '<strong>Peak-season blackout:</strong> Some 5-star and luxury houseboat bookings during Christmas, New Year and Eid may be 100% non-refundable from the date of confirmation.',
        '<strong>Force majeure:</strong> If a trip is disrupted by natural disaster, government restriction, road closure or unrest, we will offer rescheduling or credit notes valid for 12 months.',
        '<strong>Modifications:</strong> Date changes 30+ days before travel are usually allowed for a small admin fee, subject to hotel availability and revised pricing.',
        '<strong>Partial cancellations:</strong> If 1–2 travellers from a group drop out, package per-person cost may be recalculated based on the new group size.',
      ],
    },
    {
      heading: 'Travel Insurance Recommendation',
      paragraphs: [
        'For high-value <strong>Kashmir tour packages</strong>, especially those involving flights and Leh-Ladakh extensions, we strongly recommend buying travel insurance that covers trip cancellation, medical emergencies, baggage loss and weather-related disruptions. We can recommend trusted insurance partners on request.',
      ],
    },
  ],
  faqs: [
    {
      q: 'How long does a refund take to credit?',
      a: 'Once cancellation is confirmed, refunds are processed within 7–10 business days. Depending on your bank and payment method, it may take an additional 5–7 days to reflect in your account.',
    },
    {
      q: 'Are flights refundable?',
      a: 'Flight refunds depend on the airline and fare class — usually they follow the airline’s own cancellation policy and are processed separately from the land package refund.',
    },
    {
      q: 'What if my Kashmir trip is cancelled due to bad weather?',
      a: 'In case of force majeure cancellations, we offer free rescheduling or a credit note valid for 12 months. Refunds for non-refundable third-party services are handled as per their respective policies.',
    },
  ],
};

export const termsSEO = {
  title: 'Booking Terms — Clear Expectations on Both Sides',
  intro: [
    'These terms govern every booking made with Velmora Holidays — the <strong>best travel agency in Kashmir</strong>. By confirming a <strong>Kashmir tour package</strong>, you agree to the conditions listed in the full Terms & Conditions above. The summary below is meant to help you quickly understand what to expect.',
  ],
  sections: [
    {
      heading: 'Quick Summary',
      list: [
        '<strong>Booking confirmation:</strong> Bookings are confirmed only after receipt of advance payment (25% by default).',
        '<strong>Balance payment:</strong> Due 7 days before travel; for last-minute bookings, full payment is required upfront.',
        '<strong>ID proof:</strong> Government photo ID is mandatory for all travellers at hotel check-ins.',
        '<strong>Itinerary changes:</strong> We may modify the itinerary due to weather, road closures or safety reasons; equivalent alternatives are provided wherever possible.',
        '<strong>Liability:</strong> Velmora Holidays acts as a booking agent for hotels, transport and activity operators. Travel insurance is strongly recommended.',
      ],
    },
    {
      heading: 'Responsible Travel',
      paragraphs: [
        'We expect travellers to respect the local culture, environment and rules in Kashmir and Ladakh. Littering, harming wildlife, drone use without permission, and disrespecting religious sites are not acceptable on any of our tours. Our drivers and guides are empowered to politely flag and stop such behaviour.',
      ],
    },
  ],
  faqs: [
    {
      q: 'Do I need to sign a contract before my Kashmir trip?',
      a: 'No physical contract is required. Confirming the package and paying the advance constitutes acceptance of our Terms & Conditions and the booking voucher we share with you.',
    },
    {
      q: 'Can the itinerary change once it is confirmed?',
      a: 'Confirmed itineraries are honoured wherever possible. They may change only due to weather, road closures, government restrictions or safety reasons — and we always offer equivalent alternatives.',
    },
    {
      q: 'Are children charged the same as adults?',
      a: 'Children below 5 years usually travel free without an extra bed; ages 5–11 are charged at child-with-bed rates depending on the hotel. Exact charges are mentioned in your custom quote.',
    },
  ],
};
